import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from '../../components/DatePicker';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import clsx from 'clsx';
import TakeoffArrow from '../../components/TakeoffArrow';
import LandingArrow from '../../components/LandingArrow';
import { useDeliverBy } from '../../hooks/useDeliverBy';
import { useServiceOptions, createServiceOptionsPayload } from '../../hooks/useServiceOptions';
import LoadingState from 'components/ShippingFlow/components/LoadingState';
import DeliveryServiceSelector from './DeliveryServiceSelector';
import { calcDeliverByDate, calcNextShipDate } from '../../utils/dates';
import { useFormStore } from '../../stores/formData';
import { recalcServiceWithProductQuantities } from '../../utils/pricing';
import HourlyPickupSelector from '../../components/HourlyPickupSelector';
import InfoText from '../../components/InfoText';
import AlternatePickupDateSelector from './AlternatePickupDateSelector';

const DeliverySection = ({ 
  legIndex,
  blockedDates = [],
  blockedDaysOfWeek = [],
  minDate = new Date(),
  maxDate = null,
  showEmbarkSection = false,
  showPickupTimeSection = false,
  blockWeekends = false, // New prop to control weekend blocking
  onSetShipDate = null,
  onSetDeliveryDate = null,
  onServiceSelected = null,
  onServicesLoaded = null 
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // const [shipDate, setShipDate] = useState(null);
  // const [deliveryDate, setDeliveryDate] = useState(null);

  const [embarkDate, setEmbarkDate] = useState(null);
  const [disembarkDate, setDisembarkDate] = useState(null);

  const [deliverByOptions, setDeliverByOptions] = useState([]);
  const [pickupByOptions, setPickupByOptions] = useState([]);
  const [noDeliveryByOptionsAvailable, setNoDeliveryByOptionsAvailable] = useState(false);  

  // const [selectedService, setSelectedService] = useState(null);
  //const [embarkDate, setEmbarkDate] = useState('5days');
  const [customDate, setCustomDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('1pm - 5pm (free)');
  const [canLoadServices, setCanLoadServices] = useState(false);
  const [debouncedQuantities, setDebouncedQuantities] = useState({});
  const [lastServiceSelectionTime, setLastServiceSelectionTime] = useState(0);
  const [lastCall, setLastCall] = useState(0);
  const [previousProductCount, setPreviousProductCount] = useState(0);

  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);
  const origin = useFormStore((state) => state.formData.legs?.[legIndex]?.fromAddress);
  const destination = useFormStore((state) => state.formData.legs?.[legIndex]?.toAddress);
  const currentLeg = useFormStore((state) => state.formData.legs?.[legIndex]);
  const { brandId, authToken } = useFormStore((state) => state.formData);
  const cruiseSelection = useFormStore((state) => state.formData.legs?.[legIndex]?.cruiseSelection);
  const hourlyPickupFees = useFormStore((state) => state.formData.hourlyPickupFees);
  
  const { 
    fetchServiceOptions: fetchPickupByServices,
    outboundLoading: pickupByServiceOptionsLoading,
    outboundError: pickupByServiceOptionsError,

  } = useServiceOptions();

  const {
    serviceOptions: deliverByServices,
    isLoading: deliverByServiceOptionsLoading,
    error: deliverByServiceOptionsError,
    formatPrice,
  } = useDeliverBy(helpers.getLeg(legIndex), {
    brand_id: helpers.getBrandId(),
    combine: false,
    direction: legIndex == 0 ? 'outbound' : 'return',
    enabled: canLoadServices
  });


  useEffect(() => {
    const currentLeg = helpers.getLeg(legIndex);
    if (hourlyPickupFees &&
        Array.isArray(hourlyPickupFees) &&
        currentLeg?.selectedPickupFee == null) {
     // default to the first pickup fee
     actions.leg.updateField(legIndex, 'selectedPickupFee', hourlyPickupFees[0]);
    }
  }, [hourlyPickupFees]);

  // Debouncer
  useEffect(() => {
    const currentProductCount = currentLeg?.selectedProducts?.length || 0;

    if(cruiseSelection.pickup && currentLeg.shipDate && !currentLeg.deliveryDate) {
      // should use pickup by
       setCanLoadServices(false);
       return;
    }

    if(!currentLeg.shipDate && currentLeg.deliveryDate) {
      setCanLoadServices(true);
      setPreviousProductCount(currentProductCount);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedQuantities(currentLeg?.selectedProducts?.reduce((acc, product) => {
        acc[product.id] = product.quantity || 0;
        return acc;
      }, {}) || {});
      setCanLoadServices(true);
      setPreviousProductCount(currentProductCount);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [currentLeg, cruiseSelection]);

  // Combine blocked days of week with weekend blocking
  const effectiveBlockedDaysOfWeek = useMemo(() => {
    const blocked = [...blockedDaysOfWeek];
    if (blockWeekends) {
      // Add Saturday (6) and Sunday (0) if not already blocked
      if (!blocked.includes(0)) blocked.push(0); // Sunday
      if (!blocked.includes(6)) blocked.push(6); // Saturday
    }
    return blocked;
  }, [blockedDaysOfWeek, blockWeekends]);

  useEffect(() => {
    if(!deliverByServiceOptionsLoading &&
      deliverByServices && Array.isArray(deliverByServices)) {
       if (deliverByServices.length > 0) {
        // Handle the available service options
        setDeliverByOptions(deliverByServices);
        setNoDeliveryByOptionsAvailable(false); 
        const leg = helpers.getLeg(legIndex);
        if(leg.selectedService) {
          const updatedService = deliverByServices.find(s => s.display_name === leg.selectedService.display_name);
          if(updatedService) {
            // the shipdate was probably cleared, so make sure it is set too
            actions.leg.updateFields(legIndex, {
              selectedService: updatedService,
              shipDate: updatedService.ship_date,
              lastServiceSelectionTime: Date.now()
            });
          }
        }
      } else {
        setDeliverByOptions([]);
        actions.leg.updateField(legIndex, 'selectedService', null);
        setNoDeliveryByOptionsAvailable(true);
      }
    }
  },[deliverByServices, deliverByServiceOptionsLoading]);


  useEffect(() => {
    // we need to fetch outbound services
    const quantityMap = currentLeg?.selectedProducts?.reduce((acc, product) => {
      acc[product.product_id] = product.quantity || 0;
      return acc;
    }, {}) || {};

    const totalQuantity = Object.values(quantityMap).reduce((sum, qty) => sum + qty, 0);

    // the product ids are the keys of the map
    const productIds = Object.keys(quantityMap);
    if(!productIds?.length) return;

    const existingOption  = pickupByOptions.find(option => {
      return option.display_name === currentLeg.selectedService?.display_name;
    });
    if(existingOption && totalQuantity === existingOption.package_count)
      return; // no need to refetch
    
    const canFetchOutbound = pickupByOptions?.length > 0 || (
      cruiseSelection?.pickup &&
      currentLeg?.shipDate
    );

    if(!canFetchOutbound) return;

    const fetchOutboundServices = async () => {
      // Fetch the outbound services
      const payload = createServiceOptionsPayload({
        brandId: brandId,
        productIds,
        origin: currentLeg?.fromAddress,
        destination: currentLeg?.toAddress,
        shipDate: currentLeg?.shipDate,
        authToken: authToken
      });
      try {
        const results = await fetchPickupByServices(payload);
        const updatedResults = recalcServiceWithProductQuantities(results, quantityMap);
        setPickupByOptions(updatedResults);
      } catch (error) {
        console.error('Error fetching pickup service options:', error);
      }
    };
    fetchOutboundServices();
  }, [helpers, legIndex, currentLeg]);

  const handleAlternateShipDateChange = useCallback((date) => {
    actions.leg.updateField(legIndex, 'alternateShipDate', date);
  }, [legIndex, actions]);

  // Filter out blocked dates and days (including weekends if enabled)
  const isDateDisabled = useCallback((date) => {
    if (effectiveBlockedDaysOfWeek.includes(date.getDay())) {
      return true;
    }
    return blockedDates.some(blockedDate => 
      blockedDate.toDateString() === date.toDateString()
    );
  }, [effectiveBlockedDaysOfWeek, blockedDates]);

  const handleDeliveryDateChange = useCallback((date) => {
    actions.leg.updateFields(legIndex, {
      shipDate: null,
      arrivalDate: date,
      deliveryDate: date
    });
  }, [legIndex, actions]);

  const handleShipDateChange = useCallback((date) => {
    //setShipDate(date);
    actions.leg.updateFields(legIndex, {
      shipDate: date
    });
  }, [legIndex, actions]);

  const handleCustomDateChange = useCallback((date) => {
    setCustomDate(date);
    setEmbarkDate('custom');
  }, []);

  const handleServiceSelect = useCallback((service) => {
    //setSelectedService(service);
    onServiceSelected?.(legIndex, service);
  }, [legIndex, onServiceSelected]);

  const handleEmbarkDateChange = useCallback((date) => {
    setNoDeliveryByOptionsAvailable(false);
    setEmbarkDate(date);
    const deliveryDate = calcDeliverByDate(date);
    actions.leg.updateFields(legIndex, {
      shipDate: null,
      embarkDate: date,
      arrivalDate: deliveryDate,
      deliveryDate: deliveryDate,
      availableServices: [],
      selectedService: null
    });
  }, [legIndex, actions]);

  const handleDisembarkDateChange = useCallback((date) => {
    setDisembarkDate(date);

    // add one business day
    const nextShipDate = calcNextShipDate(date);
    // setShipDate(nextShipDate);
    setPickupByOptions([]);
    
    actions.leg.updateFields(legIndex, {
      selectedService: null,
      disembarkDate: date,
      shipDate: nextShipDate,
      deliveryDate: null,
      arrivalDate: null
    });
  }, [legIndex, actions]);

  const handleSelectedPickupFeeId = useCallback((fee) => {
  }, [currentLeg, legIndex, helpers]);

  const handlePickupTimeChange = useCallback((fee) => {
    console.log('Selected Pickup Time', fee);
    if(fee?.id) {
      actions.leg.updateField(legIndex, 'selectedPickupFee', fee);
    }
  }, [legIndex, actions]);

  // Theme-based styling
  const cardClassName = clsx(
    'rounded-lg py-5 px-2 md:px-6',
    {
      'bg-slate-800 text-white': isDark,
      'bg-slate-200 text-gray-900': !isDark
    }
  );

  const sectionClassName = clsx(
    'px-2 md:px-3 mb-4',
    {
      'bg-slate-700/30': isDark,
      'bg-slate-200': !isDark
    }
  );

  const titleClassName = clsx(
    'text-medium font-semibold flex items-center gap-2 mb-4',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const labelClassName = clsx(
    'text-sm font-medium mb-3',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const iconClassName = clsx({
    'text-slate-400': isDark,
    'text-gray-500': !isDark
  });

  const serviceCardClassName = (isSelected) => clsx(
    'w-full p-4 rounded-xl border-2 text-left transition-all duration-200 mb-3',
    {
      // Dark theme
      'border-slate-600 bg-slate-700/50 hover:bg-slate-700': isDark && !isSelected,
      'border-green-500 bg-green-500/10': isDark && isSelected,
      // Light theme
      'border-gray-200 bg-gray-50 hover:bg-gray-100': !isDark && !isSelected, 
      'border-green-500 bg-green-50': !isDark && isSelected
    }
  );

  const radioClassName = clsx(
    'w-5 h-5 mr-3 flex-shrink-0',
    {
      'text-blue-500': isDark,
      'text-blue-600': !isDark
    }
  );

  const serviceTitleClassName = clsx(
    'font-semibold text-sm text-base',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const serviceDateClassName = clsx(
    'text-sm',
    {
      'text-slate-300': isDark,
      'text-gray-600': !isDark
    }
  );

  const priceClassName = clsx(
    'text-lg font-bold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const embarkButtonClassName = (isActive) => clsx(
    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
    {
      'bg-white text-slate-900': isDark && isActive,
      'bg-slate-700 text-slate-300 hover:bg-slate-600': isDark && !isActive,
      'bg-blue-500 text-white': !isDark && isActive,
      'bg-gray-200 text-gray-700 hover:bg-gray-300': !isDark && !isActive
    }
  );

  const selectClassName = clsx(
    'w-full rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500',
    {
      'bg-slate-600 text-white': isDark,
      'bg-white text-gray-900 border border-gray-300': !isDark
    }
  );

  // get these from state

  const pickupLocationHeader = useMemo(() => {
    return origin ? <div className="flex items-center gap-2">
      <TakeoffArrow size={20} className={iconClassName} />
      Pickup in {origin.cruisePort?.name || origin.city || ''}
    </div> : '';
  }, [origin]);

  const deliverByLocationHeader = useMemo(() => {
    return destination ? <div className="flex items-center gap-2">
      <LandingArrow size={20} className={iconClassName} />
      Deliver in {destination.cruisePort?.name || destination.city || ''}
    </div> : '';
  }, [destination]);

  return (
    <div data-testid={`leg${legIndex}-delivery-section`} className={cardClassName}>
      <h3 className="text-xl font-semibold ml-3 mb-4">Delivery & Pickup Dates</h3>
      <div className="space-y-6">
        {/* Pickup Section */}
        <div className={sectionClassName}>
          {cruiseSelection?.pickup && (
            <div className="space-y-3">
              <h3 className={titleClassName}>
                {pickupLocationHeader}
              </h3>
              <div className={labelClassName}>Disembark Date</div>
              <InfoText>
                Your bags will be picked up on the disembark date and delivered to your destination.
              </InfoText>
              <div className="relative">
                <DatePicker
                  onChange={handleDisembarkDateChange}
                  //filterDate={(date) => !isDateDisabled(date)}
                  minDate={minDate || new Date()}
                  dateFormat="MMM d, yyyy"
                  selected={currentLeg?.disembarkDate || disembarkDate}
                  placeholderText="Select your disembark date"
                  // className={datePickerClassName}
                  calendarClassName="modern-datepicker"
                  popperClassName="datepicker-popper"
                  showPopperArrow={false}
                />
                <ChevronDown 
                  className={clsx(
                    'hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
                    {
                      'text-slate-400': isDark,
                      'text-gray-500': !isDark
                    }
                  )}
                  size={16} 
                />
              </div>
            </div>
          )}
          {cruiseSelection?.delivery && (
            <div className="space-y-3">
              <h3 className={titleClassName}>
                {deliverByLocationHeader}
              </h3>
              <InfoText>
                Your bags will arrive at the embark port at least one business day before your embark date
              </InfoText>
              <div className={labelClassName}>Embark Date</div>
              <div className="relative">
                <DatePicker
                  onChange={handleEmbarkDateChange}
                  //filterDate={(date) => !isDateDisabled(date)}
                  minDate={minDate || new Date()}
                  dateFormat="MMM d, yyyy"
                  selected={currentLeg?.embarkDate || embarkDate}
                  placeholderText="Select your embark date"
                  //className={datePickerClassName}
                  calendarClassName="modern-datepicker"
                  popperClassName="datepicker-popper"
                  showPopperArrow={false}
                />
                <ChevronDown 
                  className={clsx(
                    'hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
                    {
                      'text-slate-400': isDark,
                      'text-gray-500': !isDark
                    }
                  )}
                  size={16} 
                />
              </div>
            </div> 
          )}  
          {!cruiseSelection?.pickup && !cruiseSelection?.delivery && (
            <div className="flex-col">
              <h3 className={titleClassName}>
                {deliverByLocationHeader}
              </h3>
              <div className="relative">
                <DatePicker
                  selected={currentLeg?.deliveryDate}
                  onChange={handleDeliveryDateChange}
                  //filterDate={(date) => !isDateDisabled(date)}
                  minDate={minDate || new Date()}
                  //maxDate={maxDate}
                  placeholderText="May 6, 2026"
                  dateFormat="MMM d, yyyy"
                  //className={datePickerClassName}
                  calendarClassName="modern-datepicker"
                  popperClassName="datepicker-popper"
                  showPopperArrow={false}
                />
                <ChevronDown 
                  className={clsx(
                    'hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
                    {
                      'text-slate-400': isDark,
                      'text-gray-500': !isDark
                    }
                  )}
                  size={16} 
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Delivery Section */}
        {cruiseSelection?.delivery && (
          <div className={sectionClassName}>
            <h3 className={titleClassName}>
              {pickupLocationHeader}
            </h3>

            <div className="space-y-3">
              {deliverByOptions?.length > 0 && !deliverByServiceOptionsLoading && !deliverByServiceOptionsError && (
                <InfoText>
                  Based on your embark date, these pick up dates will guarantee your bags will arrive on time.
                </InfoText>
              )}
              <DeliveryServiceSelector 
                legIndex={legIndex}
                deliveryDate={currentLeg?.deliveryDate}
                shipDate={currentLeg?.shipDate}
                serviceOptions={deliverByOptions}
                selectedService={currentLeg?.selectedService}
                loading={deliverByServiceOptionsLoading}
                error={deliverByServiceOptionsError}
                onServiceSelect={handleServiceSelect}
                formatPrice={formatPrice}
                labelClassName={labelClassName}
                serviceCardClassName={serviceCardClassName}
                radioClassName={radioClassName}
                serviceTitleClassName={serviceTitleClassName}
                serviceDateClassName={serviceDateClassName}
                priceClassName={priceClassName}
                LoadingComponent={LoadingState}
              />
              {noDeliveryByOptionsAvailable && deliverByOptions.length == 0 && 
                !deliverByServiceOptionsLoading && !deliverByServiceOptionsError && (
                <div className={clsx(
                  'text-sm text-slate-500 flex flex-col justify-center items-center ',
                  'border border-2 gap-3 border-slate-300 p-2'
                )}>
                  <p className="text-center">
                    The delivery date you selected is too soon for guaranteed on-time arrival.
                    <br />
                    Please choose a later date.
                  </p>
                </div>
              )}
              {deliverByOptions?.length > 0 && hourlyPickupFees?.length > 0 && (
                  <HourlyPickupSelector
                  pickupFees={hourlyPickupFees}
                  selectedPickupFeeId={currentLeg?.selectedPickupFee?.id}
                  onChange={handlePickupTimeChange}
                  placeholder="Choose pickup time"
                  className="mb-4"
                />
              )}

              {/* Alternate Pickup Date Selector - only shown when delivering to cruise and service selected */}
              {currentLeg?.selectedService && (
                <AlternatePickupDateSelector
                  legIndex={legIndex}
                  onChange={handleAlternateShipDateChange}
                  isDark={isDark}
                />
              )}
            </div> 
          </div>
        )}

        {cruiseSelection?.pickup  && (
          <div className={sectionClassName}>
            <h3 className={titleClassName}>
              {deliverByLocationHeader}
            </h3>

            <div className="space-y-3">
              <DeliveryServiceSelector 
                legIndex={legIndex}
                deliveryDate={currentLeg?.deliveryDate}
                shipDate={currentLeg?.shipDate}
                serviceOptions={pickupByOptions}
                selectedService={currentLeg?.selectedService}
                loading={pickupByServiceOptionsLoading}
                error={pickupByServiceOptionsError}
                onServiceSelect={handleServiceSelect}
                formatPrice={formatPrice}
                labelClassName={labelClassName}
                serviceCardClassName={serviceCardClassName}
                radioClassName={radioClassName}
                serviceTitleClassName={serviceTitleClassName}
                serviceDateClassName={serviceDateClassName}
                priceClassName={priceClassName}
                LoadingComponent={LoadingState}
              />
            </div> 
          </div>
        )}

        {!cruiseSelection?.pickup && !cruiseSelection?.delivery && currentLeg?.deliveryDate && (
          <div className={sectionClassName}>
            <h3 className={titleClassName}>
              {pickupLocationHeader}
            </h3>

            <div className="space-y-3">
              {deliverByOptions?.length > 0 && !deliverByServiceOptionsLoading && !deliverByServiceOptionsError && (
                <InfoText>
                  Based on your delivery date, these pick up dates will guarantee your bags will arrive on time.
                </InfoText>
              )}
              <DeliveryServiceSelector 
                legIndex={legIndex}
                deliveryDate={currentLeg?.deliveryDate}
                shipDate={currentLeg?.shipDate}
                serviceOptions={deliverByOptions}
                selectedService={currentLeg?.selectedService}
                loading={deliverByServiceOptionsLoading}
                error={deliverByServiceOptionsError}
                onServiceSelect={handleServiceSelect}
                formatPrice={formatPrice}
                labelClassName={labelClassName}
                serviceCardClassName={serviceCardClassName}
                radioClassName={radioClassName}
                serviceTitleClassName={serviceTitleClassName}
                serviceDateClassName={serviceDateClassName}
                priceClassName={priceClassName}
                LoadingComponent={LoadingState}
              />
              {noDeliveryByOptionsAvailable && deliverByOptions.length == 0 && 
                !deliverByServiceOptionsLoading && !deliverByServiceOptionsError && (
                <div className={clsx(
                  'text-sm text-slate-500 flex flex-col justify-center items-center ',
                  'border border-2 gap-3 border-slate-300 p-2'
                )}>
                  <p className="text-center">
                    The delivery date you selected is too soon for guaranteed on-time arrival.
                    <br />
                    Please choose a later date.
                  </p>
                </div>
              )}
              {deliverByOptions?.length > 0 && hourlyPickupFees?.length > 0 && (
                <HourlyPickupSelector
                  pickupFees={hourlyPickupFees}
                  selectedPickupFeeId={currentLeg?.selectedPickupFee?.id}
                  onChange={handlePickupTimeChange}
                  placeholder="Select pickup time"
                  className="mb-4"
                />
              )}
            </div> 
          </div>
        )}        
      </div>
    </div>
  );
};

DeliverySection.propTypes = {
  legIndex: PropTypes.number.isRequired,
  blockedDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  blockedDaysOfWeek: PropTypes.arrayOf(PropTypes.number),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  showEmbarkSection: PropTypes.bool,
  showPickupTimeSection: PropTypes.bool,
  blockWeekends: PropTypes.bool, // New prop
  onServicesLoaded: PropTypes.func,
  onServiceSelected: PropTypes.func,
  onSetDeliveryDate: PropTypes.func,
  onSetShipDate: PropTypes.func,
};

export default DeliverySection;